/* eslint-disable @atlassian/tangerine/import/no-parent-imports */
// Note: The above eslint-disable is needed because this module imports from parent directories.
// This is acceptable here as query-plan is tightly coupled to search-page-relay.
import { useEffect, useRef } from 'react';

import deepEqual from 'deep-equal';
import { type UseQueryLoaderLoadQueryOptions, useRelayEnvironment } from 'react-relay';
import { createOperationDescriptor, getRequest, type IEnvironment } from 'relay-runtime';

import { fg } from '@atlaskit/platform-feature-flags';
import { addUFOCustomCohortData } from '@atlaskit/react-ufo/custom-cohort-data';
import type { BackendExperiment } from '@atlassian/search-experiment/types';

import { SEARCH_PAGE } from '../../../common/constants/experience';
import type { AllFilterStates } from '../../../common/constants/filters/types';
import type { PrimaryProductKey } from '../../../common/constants/product-keys';
import type { BootstrapVariables, SearchVariables } from '../../../common/constants/relay/types';
import { ResultsStates } from '../../../common/constants/results';
import type {
	ProductKeys,
	SearchQueryStringParams,
} from '../../../common/constants/schemas/query-params';
import type { SearchPreloadDataProvider } from '../../../common/types/search-preload';
import { searchResultsLoadUFOExperienceStart } from '../../../common/ui/ufo/experiences';
import { getSessionIdFromLocalStorage } from '../../../common/utils/events/get-session-id-from-local-storage';
import { serveFederatedSlackExperiment } from '../../../common/utils/experiments/experiment-utils';
import { getApplicationMode } from '../../../common/utils/get-application-mode';
import { getLeapQueryMemo } from '../../../common/utils/get-leap-query-memo';
import { isLeapQueryEnabled } from '../../../common/utils/is-leap-query-enabled';
import { ASYNC_QUERY, recordQueryDispatch } from '../../../common/utils/perf/query-layer-timings';
import type { QueryParams } from '../../../common/utils/query-params/types';
import { startReSearchTransition } from '../../../common/utils/re-search-transition';
import { getBootstrapVariables } from '../../../common/utils/relay/get-bootstrap-variables';
import { getSearchVariables } from '../../../common/utils/relay/get-search-variables';
import { setQueryUpdatedTime } from '../../../common/utils/relay/query-updated-time';
import { useBootstrapState } from '../../../controllers/store/bootstrap';
import { useSearchState } from '../../../controllers/store/search';
import { generateSearchInputs } from '../../../services/search-query-generator';
import type { rightPanelAsyncQuery$variables } from '../right-panel/__generated__/rightPanelAsyncQuery.graphql';
import type { rightPanelQuery$variables } from '../right-panel/__generated__/rightPanelQuery.graphql';
import SearchResultsAsyncQuery, {
	type searchResultsAsyncQuery$variables,
} from '../search-results/__generated__/searchResultsAsyncQuery.graphql';
import type { searchResultsCqlQuery$variables } from '../search-results/__generated__/searchResultsCqlQuery.graphql';
import { getMaxResultsToShow } from '../utils/get-max-results-to-show';
import { getRelayFetchOptions } from '../utils/get-relay-fetch-options';
import { getSearchPageMode, type SearchPageMode } from '../utils/get-search-page-mode';
import {
	type ReportPreloadQueryVarsDiff,
	usePreloadQueryVarsDiffReporter,
} from '../utils/preload-query-vars-diff';
import { type ReportPreloadTimings, usePreloadTimingsReporter } from '../utils/preload-timings';
import { SEARCH_SESSION_ID } from '../utils/search-session-id';
import {
	type FullPageSearchVitalStatsAPI,
	useFullPageSearchVitalStats,
} from '../vital-stats-context';

type VoidFunc = () => void;

type Loader<TVariables> = (variables: TVariables, options?: UseQueryLoaderLoadQueryOptions) => void;

export type UseQueryPlanArgs = {
	// Query loaders / disposers
	loadSearchResultsAsyncQuery: Loader<searchResultsAsyncQuery$variables>;
	disposeSearchResultsAsyncQuery: VoidFunc;
	loadRightPanelQuery?: Loader<rightPanelQuery$variables>;
	disposeRightPanelQuery?: VoidFunc;
	loadRightPanelAsyncQuery?: Loader<rightPanelAsyncQuery$variables>;
	disposeRightPanelAsyncQuery?: VoidFunc;
	loadSearchResultsCQLQuery: Loader<searchResultsCqlQuery$variables>;
	disposeSearchResultsCQLQuery: VoidFunc;

	// Local state mirrors
	setBootstrap: (bootstrap: BootstrapVariables) => void;
	setSearch: (search: SearchVariables) => void;

	// UI/metrics side effects
	setResultsState: (state: ResultsStates) => void;
	resetErrorBoundary?: VoidFunc;
	bodyRef: React.RefObject<HTMLDivElement>;
	setMaxResultsToShow: React.Dispatch<React.SetStateAction<number | undefined>>;
	backendExperimentRef: React.MutableRefObject<BackendExperiment | undefined>;
	preloadDataProvider?: SearchPreloadDataProvider;

	// Leap query inputs (passed from the UI layer to avoid subscribing to more stores here).
	isAdminHubAIEnabled?: boolean;
	isRovoLLMEnabled?: boolean;
	logError: (error: Error) => void | Promise<void>;

	// Search request dependencies derived from query params (independent of search store)
	cql: string | undefined;
	searchQuery: string;
	filters: Record<string, AllFilterStates | undefined>;
	selectedProducts: ProductKeys[];

	queryParams: QueryParams;

	// Flag used to determine various lite-mode specific Search features like querying for extra Jira node edges to render table views in Full-page Search
	isLiteMode: boolean;
};

export type UseQueryPlanResult = {
	// The orchestrator-derived mode used by rendering to decide Landing vs Results.
	mode: SearchPageMode;
	bootstrap: BootstrapVariables;
	search: SearchVariables;
};

type QueryPlan = {
	mode: SearchPageMode;
	bootstrap: BootstrapVariables;
	search: SearchVariables;
	cql: SearchQueryStringParams['cql'] | undefined;
	selectedProducts: ProductKeys[];
	queryParams: QueryParams;
};

type ExecuteQueryPlanArgs = {
	plan: QueryPlan;
	loadSearchResultsAsyncQuery: Loader<searchResultsAsyncQuery$variables>;
	loadRightPanelQuery?: Loader<rightPanelQuery$variables>;
	loadRightPanelAsyncQuery?: Loader<rightPanelAsyncQuery$variables>;
	loadSearchResultsCQLQuery: Loader<searchResultsCqlQuery$variables>;
	disposeSearchResultsAsyncQuery: VoidFunc;
	disposeSearchResultsCQLQuery: VoidFunc;
	disposeRightPanelQuery?: VoidFunc;
	disposeRightPanelAsyncQuery?: VoidFunc;
	setBootstrap: (bootstrap: BootstrapVariables) => void;
	setSearch: (search: SearchVariables) => void;
	setResultsState: (state: ResultsStates) => void;
	resetErrorBoundary?: VoidFunc;
	setMaxResultsToShow: React.Dispatch<React.SetStateAction<number | undefined>>;
	bodyRef: React.RefObject<HTMLDivElement>;
	backendExperimentRef: React.MutableRefObject<BackendExperiment | undefined>;
	reportPreloadQueryVarsDiff: ReportPreloadQueryVarsDiff;
	/** Relay environment, for the pre-dispatch store check. */
	environment: IEnvironment;
	reportPreloadTimings: ReportPreloadTimings;
	// VitalStats API from context
	vitalStats: FullPageSearchVitalStatsAPI;
	isAdminHubAIEnabled: boolean;
	isRovoLLMEnabled: boolean;
	isLiteMode: boolean;
	logError: (error: Error) => void | Promise<void>;
	preloadDataProvider?: SearchPreloadDataProvider;
};

/**
 * Common setup for search modes (RESULTS and CQL_RESULTS).
 * Handles task ID generation, analytics, error boundary reset, and scroll behavior.
 */
function setupSearchExecution({
	onSearchStart,
	resetErrorBoundary,
	setQueryUpdatedTime,
	bodyRef,
	setMaxResultsToShow,
	cacheAvailable,
	cacheExpected,
	isNetworkMode,
}: {
	onSearchStart: (options: {
		cacheAvailable: boolean;
		cacheExpected: boolean;
		isNetworkMode: boolean;
	}) => string;
	resetErrorBoundary?: VoidFunc;
	setQueryUpdatedTime: () => void;
	bodyRef: React.RefObject<HTMLDivElement>;
	setMaxResultsToShow: React.Dispatch<React.SetStateAction<number | undefined>>;
	cacheAvailable: boolean;
	cacheExpected: boolean;
	isNetworkMode: boolean;
}): void {
	// VitalStats: fires UX taskStart + Network taskStart (and superseded abort)
	// This also sets the UX task ID ref internally via context
	onSearchStart({ cacheAvailable, cacheExpected, isNetworkMode });

	// Note: the re-search VC90 transition is NOT started here. It is started in the query-plan
	// effect the moment a subsequent search's plan changes (before this network fetch), so it
	// measures the full formulate→results duration, and completed via
	// `releaseReSearchTransitionHold` when results render.

	// Reset error boundary and query timestamp
	resetErrorBoundary?.();
	setQueryUpdatedTime();

	// Scroll to top and update max results
	bodyRef.current?.scrollTo?.(0, 0);
	setMaxResultsToShow(getMaxResultsToShow(window.innerHeight));
}

/**
 * Load LeapQuery as a non-CQL enhancement (errors are non-fatal and should not block results).
 */
function loadLeapQuery({
	bootstrap,
	searchQuery,
	isAdminHubAIEnabled,
	isRovoLLMEnabled,
	logError,
}: {
	bootstrap: Pick<BootstrapVariables, 'primaryProduct' | 'isRovoEnabled'>;
	searchQuery: string;
	isAdminHubAIEnabled: boolean;
	isRovoLLMEnabled: boolean;
	logError: (error: Error) => void | Promise<void>;
}): void {
	const { primaryProduct, isRovoEnabled } = bootstrap;
	if (
		primaryProduct &&
		searchQuery &&
		isLeapQueryEnabled(getApplicationMode({ isRovoEnabled }), isAdminHubAIEnabled, isRovoLLMEnabled)
	) {
		// Loading state handled in the LeapQuery component.
		getLeapQueryMemo({ query: searchQuery, product: primaryProduct }).catch((e: Error) => {
			logError(e);
		});
	}
}

/**
 * Executes the query plan: loads/disposes queries, fires analytics, and updates state.
 * This is the single point where we orchestrate all query loading based on the derived mode.
 */
function executeQueryPlan({
	plan,
	loadSearchResultsAsyncQuery,
	loadRightPanelQuery,
	loadRightPanelAsyncQuery,
	loadSearchResultsCQLQuery,
	disposeSearchResultsAsyncQuery,
	disposeSearchResultsCQLQuery,
	disposeRightPanelQuery,
	disposeRightPanelAsyncQuery,
	setBootstrap,
	setSearch,
	setResultsState,
	resetErrorBoundary,
	setMaxResultsToShow,
	bodyRef,
	backendExperimentRef,
	reportPreloadQueryVarsDiff,
	environment,
	reportPreloadTimings,
	vitalStats,
	isAdminHubAIEnabled,
	isRovoLLMEnabled,
	isLiteMode,
	logError,
	preloadDataProvider,
}: ExecuteQueryPlanArgs): void {
	const { mode, bootstrap, search, cql, selectedProducts, queryParams } = plan;

	const relayFetchOptions = getRelayFetchOptions(bootstrap);

	// RESULTS: load results + counts + leapQuery; ensure CQL ref is disposed.
	if (mode === 'RESULTS') {
		disposeSearchResultsCQLQuery();

		const { searchResultsQueryVariables, rightPanelQueryVariables, backendExperiment } =
			generateSearchInputs({
				backendExperimentOverrides: bootstrap.overrides?.backendExperiments || [],
				cloudId: bootstrap.cloudId || '',
				user: bootstrap.user,
				primaryProduct: bootstrap.primaryProduct as PrimaryProductKey, // Guaranteed by bootstrap being initialised
				experimentLayers: bootstrap.experimentLayers,
				experience: SEARCH_PAGE,
				thirdPartyConfigs: bootstrap.thirdPartyConfigs,
				products: bootstrap.products,
				productEdition: bootstrap.productEdition || {},
				productDataRegions: bootstrap.productDataRegions || {},
				isJiraIssueIndexed: bootstrap.isJiraIssueIndexed,
				sessionId: getSessionIdFromLocalStorage(),
				searchSessionId: bootstrap.searchSessionId,
				queryParams,
				jsmIncidentFilterIds: search.jsmIncidentFilterIds, // This is the last argument that comes from the search store
				onError: logError,
				isLiteMode,
			});

		serveFederatedSlackExperiment(
			undefined,
			selectedProducts,
			{ fireExperimentExposure: true },
			bootstrap.thirdPartyConfigs,
		);

		if (backendExperiment) {
			backendExperimentRef.current = backendExperiment;
		}

		searchResultsLoadUFOExperienceStart();

		// Used for smart answers to track results state.
		setResultsState(ResultsStates.IS_LOADING);

		// Whether Relay can satisfy this query from its store, captured before
		// `loadSearchResultsAsyncQuery` — afterwards the store is populated and the
		// answer changes. `loadQuery` makes the same call internally to decide
		// whether to fetch at all under `store-or-network`, so this mirrors its
		// decision rather than inferring it: `available` means no request is issued.
		//
		// Gated because `check` walks the operation's selections and `loadQuery`
		// repeats the same call, so this adds a store traversal per dispatch on the
		// very path it measures. Gate off is exactly the previous behaviour: no
		// cohort key, and `cacheAvailable` left false.
		let cacheAvailable = false;
		if (fg('rovo-search-query-store-status')) {
			const storeStatus = environment.check(
				createOperationDescriptor(
					getRequest(SearchResultsAsyncQuery),
					searchResultsQueryVariables,
					relayFetchOptions.networkCacheConfig,
				),
			).status;

			addUFOCustomCohortData('asyncQueryStoreStatus', storeStatus);
			cacheAvailable = storeStatus === 'available';
		}

		// A cache hit is expected when the preload data provider has search query variables
		// from a successful preload (e.g. SSR or quick-find)
		const cacheExpected = Boolean(
			preloadDataProvider?.getPreloadedSearch({ cloudId: bootstrap.cloudId || '' })?.search
				?.queryVariables,
		);

		setupSearchExecution({
			onSearchStart: vitalStats.onSearchStart,
			resetErrorBoundary,
			setQueryUpdatedTime,
			bodyRef,
			setMaxResultsToShow,
			cacheAvailable,
			cacheExpected,
			isNetworkMode: true, // RESULTS mode uses network VitalStats
		});

		// Captured immediately before the retrieval query is issued, so both the
		// query-layer dispatch mark and the preload → retrieval measurement below
		// exclude the reporting work that follows it.
		const dispatchAt = performance.now();
		recordQueryDispatch(ASYNC_QUERY, dispatchAt, { cacheAvailable });

		loadSearchResultsAsyncQuery(searchResultsQueryVariables, relayFetchOptions);
		disposeRightPanelQuery?.();
		loadRightPanelAsyncQuery?.(rightPanelQueryVariables, relayFetchOptions);
		// Load LeapQuery after kicking off the core queries.
		loadLeapQuery({
			bootstrap,
			searchQuery: search.searchQuery,
			isAdminHubAIEnabled,
			isRovoLLMEnabled,
			logError,
		});

		reportPreloadQueryVarsDiff({
			cloudId: bootstrap.cloudId,
			searchResultsQueryVariables,
			rightPanelQueryVariables,
		});

		// Must run before the preload data provider is cleared below — the timings
		// live on the preload payload. `asyncQueryDispatchAt`, recorded above from
		// this same `dispatchAt`, doubles as this navigation's dispatch mark.
		reportPreloadTimings({ cloudId: bootstrap.cloudId });

		// Once we have reported the diff for the Quick Find async preload, we can
		// clear the preload data provider so it can be re-warmed on the next
		// Quick Find→search-page transition. We also clear the SEARCH_SESSION_ID
		// so that it isn't reused the next time the search page is visited.
		if (bootstrap.cloudId && preloadDataProvider) {
			preloadDataProvider.clearPreloadedSearch({ cloudId: bootstrap.cloudId });
			SEARCH_SESSION_ID.dispose();
		}
	}

	// CQL_RESULTS: load CQL results; ensure non-CQL refs are disposed.
	if (mode === 'CQL_RESULTS') {
		disposeRightPanelQuery?.();
		disposeRightPanelAsyncQuery?.();
		disposeSearchResultsAsyncQuery();

		setupSearchExecution({
			onSearchStart: vitalStats.onSearchStart,
			resetErrorBoundary,
			setQueryUpdatedTime,
			bodyRef,
			setMaxResultsToShow,
			cacheAvailable: false, // CQL mode doesn't track cache
			cacheExpected: false, // CQL mode doesn't use preloading
			isNetworkMode: false, // CQL_RESULTS mode is out of scope for network VitalStats
		});
		// `mode` guarantees cql exists here.
		loadSearchResultsCQLQuery({ cql: cql as string, first: 30, after: null }, relayFetchOptions);
	}

	// LANDING: invalidate all query refs so stale results can't render/suspend.
	if (mode === 'LANDING') {
		disposeSearchResultsAsyncQuery();
		disposeRightPanelQuery?.();
		disposeRightPanelAsyncQuery?.();
		disposeSearchResultsCQLQuery();
	}

	// Keep local mirrors in sync for rendering.
	setSearch(search);
	setBootstrap(bootstrap);
}

/**
 * This query plan hook handles the lifecycle of search requests being fired
 * 	1. Derive mode based on bootstrap and search variables
 * 	2. Load/dispose Relay queries based on the mode
 * 	3. Start VitalStats and update local mirrors
 *
 * As part of M3 of the async query work, this hook will be accepting data that is part
 * of the search store as props. When these props are defined, they will be used as the
 * source of truth for search requests. Indeed, this data will essentially be a duplicate of the
 * data coming from useSearchState, however, the difference is that they will not be sourced
 * from the search store. Eventually, we will no longer need variables from the search store
 * to make a search request. It is also intention that this is not wrapped as an object called
 * 'search' as we want to move away from the idea of a search store. Rather, they are just piece
 * of data that are prop drilled. This data will typically be derived from the query parameters,
 * or fetched from the server.
 *
 * To make this clear, the following search store variables are passed as props:
 * - cql
 */
export function useQueryPlan({
	loadSearchResultsAsyncQuery,
	disposeSearchResultsAsyncQuery,
	loadRightPanelQuery,
	disposeRightPanelQuery,
	loadRightPanelAsyncQuery,
	disposeRightPanelAsyncQuery,
	loadSearchResultsCQLQuery,
	disposeSearchResultsCQLQuery,
	setBootstrap,
	setSearch,
	setResultsState,
	resetErrorBoundary,
	bodyRef,
	setMaxResultsToShow,
	backendExperimentRef,
	preloadDataProvider,
	isAdminHubAIEnabled = false,
	isRovoLLMEnabled = false,
	logError,
	cql,
	searchQuery,
	filters,
	selectedProducts,
	queryParams,
	isLiteMode,
}: UseQueryPlanArgs): UseQueryPlanResult {
	const environment = useRelayEnvironment();
	const vitalStats = useFullPageSearchVitalStats();

	// eslint-disable-next-line @atlassian/search-common/no-use-rss-hooks
	const search: SearchVariables = useSearchState(getSearchVariables);
	const bootstrap: BootstrapVariables = useBootstrapState(getBootstrapVariables);
	const reportPreloadQueryVarsDiff = usePreloadQueryVarsDiffReporter(preloadDataProvider);
	const reportPreloadTimings = usePreloadTimingsReporter(preloadDataProvider);

	// Available products are sourced from bootstrap store/context, not local search mirror
	const availableProducts = bootstrap.products;

	// Bootstrap is ready when there are available products
	const bootstrapReady = availableProducts.length > 0;

	// Filters are considered selected when there is a selected product or any filter has a selection
	const hasAnyFilterSelected =
		Boolean(selectedProducts[0]) || Object.values(filters).some((filter) => filter?.hasSelection);

	// There should only ever be one selected product
	const selectedProduct = selectedProducts[0];

	const mode = getSearchPageMode({
		bootstrapReady,
		availableProducts,
		selectedProduct,
		searchQuery,
		hasAnyFilterSelected,
		cql,
	});

	const previousPlanRef = useRef<QueryPlan>();

	// Refs for non-reactive dependencies: always created to satisfy React's
	// rules of hooks (hooks must be called in the same order every render).
	// When the feature flag is ON, the useEffect reads from these refs instead
	// of using the values directly, so changes to store objects and callbacks
	// don't trigger unnecessary re-executions.
	const bootstrapRef = useRef(bootstrap);
	bootstrapRef.current = bootstrap;

	const searchRef = useRef(search);
	searchRef.current = search;

	const loadSearchResultsAsyncQueryRef = useRef(loadSearchResultsAsyncQuery);
	loadSearchResultsAsyncQueryRef.current = loadSearchResultsAsyncQuery;

	const loadRightPanelQueryRef = useRef(loadRightPanelQuery);
	loadRightPanelQueryRef.current = loadRightPanelQuery ?? undefined;

	const loadRightPanelAsyncQueryRef = useRef(loadRightPanelAsyncQuery);
	loadRightPanelAsyncQueryRef.current = loadRightPanelAsyncQuery ?? undefined;

	const loadSearchResultsCQLQueryRef = useRef(loadSearchResultsCQLQuery);
	loadSearchResultsCQLQueryRef.current = loadSearchResultsCQLQuery;

	const disposeSearchResultsAsyncQueryRef = useRef(disposeSearchResultsAsyncQuery);
	disposeSearchResultsAsyncQueryRef.current = disposeSearchResultsAsyncQuery;

	const disposeSearchResultsCQLQueryRef = useRef(disposeSearchResultsCQLQuery);
	disposeSearchResultsCQLQueryRef.current = disposeSearchResultsCQLQuery;

	const disposeRightPanelQueryRef = useRef(disposeRightPanelQuery);
	disposeRightPanelQueryRef.current = disposeRightPanelQuery ?? undefined;

	const disposeRightPanelAsyncQueryRef = useRef(disposeRightPanelAsyncQuery);
	disposeRightPanelAsyncQueryRef.current = disposeRightPanelAsyncQuery ?? undefined;

	const resetErrorBoundaryRef = useRef(resetErrorBoundary);
	resetErrorBoundaryRef.current = resetErrorBoundary;

	const vitalStatsRef = useRef(vitalStats);
	vitalStatsRef.current = vitalStats;

	const logErrorRef = useRef(logError);
	logErrorRef.current = logError;

	const reportPreloadQueryVarsDiffRef = useRef(reportPreloadQueryVarsDiff);
	reportPreloadQueryVarsDiffRef.current = reportPreloadQueryVarsDiff;

	const reportPreloadTimingsRef = useRef(reportPreloadTimings);
	reportPreloadTimingsRef.current = reportPreloadTimings;

	useEffect(() => {
		if (!bootstrapReady) {
			return;
		}

		const useStableDeps = fg('rovo-search-vitalstats-stable-query-plan-deps');

		// The deepEqual comparison only checks the search identity fields. bootstrap
		// and search are excluded because they contain non-search-relevant fields
		// (filter options, third-party configs, etc.) that change independently.
		// The search-relevant fields from bootstrap/search are already extracted as
		// individual variables (searchQuery, filters, selectedProducts, etc.) and
		// compared directly.
		const nextPlan: QueryPlan = {
			mode,
			bootstrap: useStableDeps ? ({} as typeof bootstrap) : bootstrap,
			search: useStableDeps ? ({} as typeof search) : search,
			cql,
			selectedProducts,
			queryParams,
		};

		if (deepEqual(previousPlanRef.current, nextPlan)) {
			return;
		}

		// A subsequent same-page search is a RESULTS plan change after the very first execution.
		// `previousPlanRef` is undefined only on the initial execution (the initial page load,
		// already traced host-side as initial=true), so a defined ref plus RESULTS mode means
		// the user changed their query or a filter — re-fire the VC90 metric as a transition.
		// CQL_RESULTS is intentionally excluded: a CQL search can only be entered via URL query
		// params on initial load / load-from-URL, so a subsequent same-page CQL re-search is not
		// possible. Started here (before the network fetch kicks off in executeQueryPlan) so it
		// measures the full formulate→results duration, and released when results render (see
		// releaseReSearchTransitionHold in main.tsx). No-op when the gate is off.
		const isSubsequentSearch = previousPlanRef.current !== undefined;
		if (isSubsequentSearch && mode === 'RESULTS') {
			startReSearchTransition();
		}

		previousPlanRef.current = nextPlan;

		executeQueryPlan({
			plan: { ...nextPlan, bootstrap: bootstrapRef.current, search: searchRef.current },
			loadSearchResultsAsyncQuery: loadSearchResultsAsyncQueryRef.current,
			loadRightPanelQuery: loadRightPanelQueryRef.current,
			loadRightPanelAsyncQuery: loadRightPanelAsyncQueryRef.current,
			loadSearchResultsCQLQuery: loadSearchResultsCQLQueryRef.current,
			disposeSearchResultsAsyncQuery: disposeSearchResultsAsyncQueryRef.current,
			disposeSearchResultsCQLQuery: disposeSearchResultsCQLQueryRef.current,
			disposeRightPanelQuery: disposeRightPanelQueryRef.current,
			disposeRightPanelAsyncQuery: disposeRightPanelAsyncQueryRef.current,
			setBootstrap,
			setSearch,
			setResultsState,
			resetErrorBoundary: resetErrorBoundaryRef.current,
			setMaxResultsToShow,
			bodyRef,
			backendExperimentRef,
			reportPreloadQueryVarsDiff: reportPreloadQueryVarsDiffRef.current,
			environment,
			reportPreloadTimings: reportPreloadTimingsRef.current,
			vitalStats: vitalStatsRef.current,
			isAdminHubAIEnabled,
			isRovoLLMEnabled,
			isLiteMode,
			logError: logErrorRef.current,
			preloadDataProvider,
		});
	}, [
		bootstrap,
		search,
		bootstrapReady,
		mode,
		loadSearchResultsAsyncQuery,
		loadRightPanelQuery,
		loadRightPanelAsyncQuery,
		loadSearchResultsCQLQuery,
		disposeSearchResultsAsyncQuery,
		disposeSearchResultsCQLQuery,
		disposeRightPanelQuery,
		disposeRightPanelAsyncQuery,
		setBootstrap,
		setSearch,
		setResultsState,
		resetErrorBoundary,
		setMaxResultsToShow,
		bodyRef,
		backendExperimentRef,
		environment,
		vitalStats,
		isAdminHubAIEnabled,
		isRovoLLMEnabled,
		isLiteMode,
		logError,
		cql,
		searchQuery,
		filters,
		selectedProducts,
		queryParams,
		preloadDataProvider,
	]);

	return { mode, bootstrap, search };
}

/**
 * Leaf orchestrator component: subscribes to RSS stores and runs query orchestration without rendering UI.
 * This prevents frequent store updates from re-rendering the heavy SearchPage UI tree.
 */
export function SearchPageQueryOrchestrator(args: UseQueryPlanArgs): null {
	useQueryPlan(args);
	return null;
}
